/**
 * A CV or LinkedIn PDF import — what the document said, and what saving it
 * writes. One rule for every portal: journey-web's wizard (Settings → Profile,
 * Your basics) and the phone's import read and save a document the same way.
 *
 *   `profileImportFrom`    — the extract endpoint's answer, as the import holds
 *                            it (nothing absent is invented);
 *   `profileImportPayload` — what a save sends: the document's own content
 *                            merged over what is on file, never the name;
 *   `profileImportCounts`  — what it found, for the review and analytics.
 *
 * Only what the document actually states is taken: an empty list never wipes
 * what the citizen already has, and a language without a stated level is left
 * out (BL-034 — never fabricate a CEFR level).
 */
import type { IExtractedProfileData } from '@tonnie/types';

/** What the document states, as the import holds it. */
export interface ProfileImport {
  firstName?: string;
  lastName?: string;
  headline?: string;
  summary?: string;
  currentRole?: string;
  experience?: Array<{
    company: string;
    role: string;
    startDate: string;
    endDate?: string;
    description?: string;
    location?: { city?: string; country?: string } | null;
  }>;
  education?: Array<{
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate?: string;
  }>;
  skills?: string[];
  languages?: Array<{ name: string; level: string }>;
  certifications?: Array<{ name: string; authority?: string; issueDate?: string }>;
  websiteUrls?: Array<{ type: string; url: string }>;
}

/** The extract endpoint's answer (`POST /onboarding/extract-profile` → `data`). */
export function profileImportFrom(data: IExtractedProfileData): ProfileImport {
  return {
    firstName: data.firstName ?? undefined,
    lastName: data.lastName ?? undefined,
    headline: data.headline ?? undefined,
    summary: data.summary ?? undefined,
    currentRole: data.currentRole ?? undefined,
    experience: (data.experience ?? []).map((exp) => ({
      company: exp.company,
      role: exp.role,
      startDate: exp.startDate ?? '',
      endDate: exp.endDate ?? undefined,
      description: exp.description ?? undefined,
      location: exp.location
        ? { city: exp.location.city ?? undefined, country: exp.location.country ?? undefined }
        : undefined,
    })),
    education: (data.education ?? []).map((edu) => ({
      institution: edu.institution,
      degree: edu.degree ?? '',
      field: edu.field ?? '',
      startDate: edu.startDate ?? '',
      endDate: edu.endDate ?? undefined,
    })),
    skills: data.skills ?? [],
    languages: (data.languages ?? []).map((lang) => ({ name: lang.name, level: lang.level ?? '' })),
    certifications: (data.certifications ?? []).map((cert) => ({
      name: cert.name,
      authority: cert.authority ?? undefined,
      issueDate: cert.issueDate ?? undefined,
    })),
    websiteUrls: (data.websiteUrls ?? []).map((url) => ({ type: url.type, url: url.url })),
  };
}

/** The skills block a profile stores (`talents.skills`, or its custom-fields copy). */
export interface ProfileImportSkills {
  role?: string;
  careerTime?: string;
  languages?: unknown[];
  hardSkills?: unknown[];
  softSkills?: unknown[];
  certifications?: unknown[];
}

/** What an import merges over — the lists and the role already on file. */
export interface ProfileImportBase {
  role: string;
  skills: ProfileImportSkills | null;
  education: unknown[];
  experience: unknown[];
  languages: unknown[];
  websiteUrls: unknown[];
}

/** The part of `GET /users/me` an import reads — `UserProfile` satisfies it. */
export interface ProfileImportSource {
  talent?: {
    jobTitle?: string | null;
    skills?: unknown;
    languages?: unknown;
    customFields?: unknown;
  } | null;
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function skillsOf(value: unknown): ProfileImportSkills | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ProfileImportSkills)
    : null;
}

/** What is on file, where the profile keeps it. */
export function profileImportBase(profile: ProfileImportSource): ProfileImportBase {
  const talent = profile.talent ?? null;
  const custom = (talent?.customFields ?? {}) as Record<string, unknown>;
  return {
    role: talent?.jobTitle ?? '',
    skills: skillsOf(talent?.skills) ?? skillsOf(custom['skills']),
    education: list(custom['education']),
    experience: list(custom['experience']),
    languages: list(talent?.languages),
    websiteUrls: list(custom['websiteUrls']),
  };
}

/**
 * What saving an import sends (`POST /users/me/onboarding/progress`): the
 * document's own content — role, skills, languages, education, experience,
 * links — merged over what is on file. Never the name: the account's name is
 * the citizen's, and a document's spelling of it does not replace it.
 */
export function profileImportPayload(
  base: ProfileImportBase,
  parsed: ProfileImport
): Record<string, unknown> {
  const experience =
    parsed.experience?.map((exp) => ({
      company: exp.company,
      role: exp.role || '',
      startDate: exp.startDate || '',
      endDate: exp.endDate,
      description: exp.description || undefined,
      location: exp.location
        ? { city: exp.location.city || '', country: exp.location.country || '' }
        : undefined,
    })) ?? [];
  const education = parsed.education ?? [];
  const languages = profileImportLanguages(parsed).saved;
  const hardSkills = parsed.skills?.map((skill) => ({ name: skill, experience: '' })) ?? [];
  const certifications =
    parsed.certifications?.map((cert) => ({
      name: cert.name,
      authority: cert.authority || '',
      issueDate: cert.issueDate,
    })) ?? [];
  const websiteUrls =
    parsed.websiteUrls
      ?.filter((url) => url?.url)
      .map((url) => ({
        type: url.type || 'other',
        url: /^https?:\/\//i.test(url.url) ? url.url : `https://${url.url}`,
      })) ?? [];

  const role = parsed.currentRole || parsed.headline || '';
  const prevSkills = base.skills ?? {};
  const savedLanguages = languages.length > 0 ? languages : base.languages;

  const payload: Record<string, unknown> = {
    education: education.length > 0 ? education : base.education,
    experience: experience.length > 0 ? experience : base.experience,
    websiteUrls: websiteUrls.length > 0 ? websiteUrls : base.websiteUrls,
    skills: {
      ...prevSkills,
      role: role || prevSkills.role || base.role || '',
      careerTime: prevSkills.careerTime || '',
      languages:
        languages.length > 0
          ? languages.map((lang) => ({ code: '', name: lang.name, level: lang.level }))
          : (prevSkills.languages ?? []),
      hardSkills: hardSkills.length > 0 ? hardSkills : (prevSkills.hardSkills ?? []),
      softSkills: prevSkills.softSkills ?? [],
      ...(certifications.length > 0 && { certifications }),
    },
  };
  const savedRole = role || base.role;
  if (savedRole.trim()) payload['role'] = savedRole;
  if (savedLanguages.length > 0) payload['languages'] = savedLanguages;
  return payload;
}

/**
 * The document's languages: those an import saves (a name and a level — the
 * profile keeps a language only with its level) and those it cannot, named so
 * the review can say why rather than show them as if they were imported.
 */
export function profileImportLanguages(parsed: ProfileImport): {
  saved: Array<{ name: string; level: string }>;
  withoutLevel: string[];
} {
  const named = (parsed.languages ?? []).filter((lang) => lang?.name);
  return {
    saved: named
      .filter((lang) => lang.level)
      .map((lang) => ({ name: lang.name, level: lang.level })),
    withoutLevel: named.filter((lang) => !lang.level).map((lang) => lang.name),
  };
}

export interface ProfileImportCounts {
  firstName: boolean;
  lastName: boolean;
  role: boolean;
  experience: number;
  education: number;
  languages: number;
  skills: number;
  certifications: number;
  websiteUrls: number;
}

/** What an import found, for the review and analytics. */
export function profileImportCounts(parsed: ProfileImport): ProfileImportCounts {
  return {
    firstName: !!parsed.firstName,
    lastName: !!parsed.lastName,
    role: !!(parsed.currentRole || parsed.headline),
    experience: parsed.experience?.length ?? 0,
    education: parsed.education?.length ?? 0,
    languages: profileImportLanguages(parsed).saved.length,
    skills: parsed.skills?.length ?? 0,
    certifications: parsed.certifications?.length ?? 0,
    websiteUrls: parsed.websiteUrls?.filter((url) => url?.url).length ?? 0,
  };
}

/** Did the document state anything an import would save? */
export function profileImportHasContent(parsed: ProfileImport): boolean {
  const counts = profileImportCounts(parsed);
  return (
    counts.role ||
    counts.experience + counts.education + counts.languages + counts.skills > 0 ||
    counts.certifications + counts.websiteUrls > 0
  );
}
